/**
 * The room conversation protocol — who owes a reply to what (#1107).
 *
 * WHY THIS IS ITS OWN MODULE. The rule used to live inside `openclaw-entry.ts`,
 * the OpenClaw plugin's entry point. Only wren and cortina run through it. The
 * three native bridges — grok (ohtani), claude (loopita) and muse (wowzer,
 * muse) — build a `SecureChannel` directly and decided replies with an entirely
 * separate rule of their own:
 *
 *     replyExpected: e.senderIsAgent === false     // "answer any human"
 *
 * So #1107 shipped to two of THREE implementations, and four of nine production
 * agents would have kept answering every un-mentioned message — the cascade,
 * half-fixed, in the half most likely to be read as "the protocol doesn't work".
 *
 * #1107 itself said the rule "lives in hermes channel/mention.py and the
 * OpenClaw plugin openclaw-entry.ts. Any change has to land in both." It named
 * two places. There were three. That is #1012/#1014/#1016 again: a rule at N
 * call sites decays to a rule at N-1, and a hand-written enumeration is always
 * missing one.
 *
 * So the rule lives here, is exported from the package root, and every
 * TypeScript client imports it. The only remaining twin is hermes'
 * `agentvault_hermes/channel/mention.py` — Python and TypeScript cannot share a
 * module — and that one is pinned by a cross-language test.
 *
 * THE PROTOCOL
 *
 *     no @              -> NOBODY replies   (speak IN the room, not TO anyone)
 *     @wren / @a @b     -> those named
 *     @all / @everyone  -> everyone, HUMANS ONLY
 *
 * Everyone still RECEIVES and READS all of it. The @ decides who owes an
 * answer, never who is allowed to see.
 */

/** Extract @mention names from plaintext. Returns lowercased names. */
export function parseMentions(text: string): string[] {
  const mentions: string[] = [];
  // Match @word at word boundary — supports multi-word via sequential matching
  const re = /@(\w[\w]*)/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    mentions.push(match[1].toLowerCase());
  }
  return mentions;
}

/** Does this agent owe a reply to this room message? (#1107)
 *
 * NOT a delivery decision — `channel.ts` records every inbound room message to
 * history before this is reached, and that history is replayed as context on
 * every turn the agent does take. A `false` here costs the agent the reply,
 * never the room.
 *
 * The rate limiter (`_roomCanReply`: 4 replies/60s, 75s cooldown) is a separate
 * mechanism and a safety net: it bounds how OFTEN an agent may answer; this
 * bounds WHETHER it owes an answer at all. Before #1107 the limiter was the
 * only brake, which is why one un-mentioned "good morning" cost 18 replies.
 */
export function owesRoomReply(
  plaintext: string,
  agentName: string,
  accountId: string,
  senderIsAgent?: boolean,
  roomDisplayName?: string,
  ownDisplayName?: string,
  selfResolved: boolean = true,
): boolean {
  const mentions = parseMentions(plaintext);
  // #1107 — @ IS THE ANSWER KEY, NOT THE DELIVERY KEY.
  //
  // This decides only whether THIS agent owes a reply. It no longer decides
  // whether the agent SEES the message: `channel.ts` appends every inbound room
  // message to history BEFORE invoking this handler, and that history is
  // injected into the LLM context as `[Recent room conversation]` on every turn
  // the agent does take. So an agent that stays quiet here still reads the room
  // and can answer about it later — which is the property the old comment
  // ("gating here made agents blind") was protecting, by a mechanism that no
  // longer requires answering everything.
  //
  // No @ at all → NOBODY owes a reply. This is the cascade brake.
  // Measured 2026-08-29, room 007: one un-mentioned "good morning" from the
  // owner produced 18 agent replies in 115 seconds, 6 of them in a minute where
  // no human spoke at all. Every reply was itself un-mentioned, so every reply
  // re-broadcast. A bare message is now how you speak IN the room without
  // speaking TO anyone.
  if (mentions.length === 0) return false;
  // @all / @everyone → everyone answers, but HUMANS ONLY. An agent that could
  // broadcast would re-open the cascade through the one door left. Agents
  // address people and each other by name; a room-wide question goes through a
  // person (Chris, 2026-08-30).
  if (mentions.includes("all") || mentions.includes("everyone")) {
    return senderIsAgent !== true;
  }
  // Check if this agent is mentioned by framework name/accountId, its AgentVault
  // room display_name, OR its hub-identity display_name (what others actually
  // address — the framework agentName is often the generic "default").
  // BUG-20260604-002 + regression 2026-07-08.
  const ownNames = ownRoomNames(agentName, accountId, roomDisplayName, ownDisplayName);
  if (mentions.some((m) => ownNames.has(m))) return true;
  // Fail-open (regression 2026-07-08, live rooms "room"/bodega): if this agent's
  // own identity could NOT be resolved (no hub display_name, no room roster entry,
  // and the generic framework name), do NOT silently drop an @-addressed message.
  // Going deaf to your own name is far worse than occasionally processing a
  // message meant for a sibling — and the rate-limiter bounds any loop. Only skip
  // an unmatched @mention when we actually KNOW our own names.
  if (!selfResolved) return true;
  // Has @mentions, none match this agent, and we know who we are — we are not
  // on the hook. We still read it (see the history note above).
  return false;
}

/** Lower-cased set of names that count as "this agent" in a room: framework
 * name + first word + accountId, plus the AgentVault room display_name (+ its
 * first word) when known. Shared by owesRoomReply + stripMentions. */
export function ownRoomNames(
  agentName: string,
  accountId: string,
  roomDisplayName?: string,
  ownDisplayName?: string,
): Set<string> {
  const names = new Set<string>();
  const add = (n: string | undefined) => {
    if (!n) return;
    const lower = n.toLowerCase();
    names.add(lower);
    names.add(lower.split(/[\s(]/)[0]);
  };
  add(agentName);
  add(accountId);
  add(roomDisplayName);
  // Hub-identity display_name (channel.ownDisplayName): resolved from the agent's
  // OWN identity at connect, independent of any specific room's roster — so the
  // agent still knows its own name when a room roster is incomplete/degraded
  // (regression 2026-07-08: selfName collapsed to "default" → went deaf to @name).
  add(ownDisplayName);
  return names;
}

/** Strip the matching @mention prefix from plaintext so the agent sees clean text. */
export function stripMentions(
  text: string,
  agentName: string,
  accountId: string,
  roomDisplayName?: string,
  ownDisplayName?: string,
): string {
  const ownNames = ownRoomNames(agentName, accountId, roomDisplayName, ownDisplayName);
  // Remove all @mentions that match this agent (case-insensitive)
  return text
    .replace(/@(\w[\w]*)/gi, (full, name) => {
      const lower = name.toLowerCase();
      if (ownNames.has(lower)) {
        return "";
      }
      // Also strip @all/@everyone since they're routing directives
      if (lower === "all" || lower === "everyone") return "";
      return full;
    })
    .replace(/^\s+/, "") // trim leading whitespace left by stripped mention
    .trim();
}
